import { useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import {
  defaultIfEmpty,
  filter,
  finalize,
  fromEvent,
  last,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { Vector2, Vector3 } from 'three';

import { useSubscriptions } from '@/package';
import { useLabelingBoxContext } from '@/provider';
import {
  useEditingLabelBoxStore,
  useToolMainCameraStore,
  useToolMenuStore,
} from '@/store';
import type { NEW__LabelingBox } from '@/util';
import { filterOrRestart, subVector, withCanvasXY, withTarget } from '@/util';

import {
  applyMoveOffsetToBox,
  getIntersectObjectsByNDC,
  getIntersectPlanePointByNDC,
  isEnabled,
  isIntersected,
  isOtherOperationRunning,
  isSideMode,
  setControlEnabled,
  validMouseEventAndCanvasElement,
} from '../util';

const useDefaultViewSideMove = ({
  setRenderBoxListById,
  disabled,
}: {
  setRenderBoxListById: (boxIdList: string[]) => void;
  disabled: boolean;
}) => {
  const { getMode } = useToolMainCameraStore();
  const { camera, controls } = useThree();
  const { setMoveOffset, clear } = useEditingLabelBoxStore();
  const { moveBoxList, selectBox, labelingBoxList } = useLabelingBoxContext();
  const labelingBoxListRef = useRef<NEW__LabelingBox[]>([]);

  const { isSelectedByToolName } = useToolMenuStore();
  const isAiAssistRunning = isSelectedByToolName('aiAssist');

  const prepareSideMove = ({
    toRenderBoxIdList,
  }: {
    toRenderBoxIdList: string[];
  }) => {
    setControlEnabled(false, controls);
    setRenderBoxListById(toRenderBoxIdList);
  };

  const cleanupSideMove = ({ toSelectBoxId }: { toSelectBoxId: string }) => {
    selectBox(toSelectBoxId);
    clear();
    setControlEnabled(true, controls);
    setRenderBoxListById([]);
  };

  const cleanupOnInvalidStream = () => {
    clear();
    setRenderBoxListById([]);
    !isOtherOperationRunning(isAiAssistRunning) &&
      setControlEnabled(true, controls);
  };

  useSubscriptions(
    () => [
      fromEvent<MouseEvent>(window, 'mousedown')
        .pipe(
          filter(() => isEnabled(disabled)),
          filter(() => isSideMode(getMode())),
          filter(validMouseEventAndCanvasElement),
          filter(() => !isOtherOperationRunning(isAiAssistRunning)),
          withTarget,
          withCanvasXY,
          map(e => {
            const intersections = getIntersectObjectsByNDC({
              ndc: new Vector2(e.canvasX, e.canvasY),
              camera,
              objects: labelingBoxListRef.current.map(v => v.boxMesh),
            });

            return { event: e, intersections };
          }),
          filterOrRestart(({ intersections }) => isIntersected(intersections)),
          map(({ intersections, event }) => {
            const mouseDownPosition = getIntersectPlanePointByNDC({
              ndc: new Vector2(event.canvasX, event.canvasY),
              camera,
              coplanarPoint: intersections[0].point,
            });

            return { mouseDownPosition, event, intersections };
          }),
          tap(({ intersections: mouseDownIntersections }) => {
            prepareSideMove({
              toRenderBoxIdList: [mouseDownIntersections[0].object.userData.id],
            });
          }),
          switchMap(
            ({ mouseDownPosition, intersections: mouseDownIntersections }) =>
              fromEvent<MouseEvent>(window, 'mousemove').pipe(
                withTarget,
                withCanvasXY,
                map(mouseMoveEvent => {
                  const mouseMovePosition = getIntersectPlanePointByNDC({
                    ndc: new Vector2(
                      mouseMoveEvent.canvasX,
                      mouseMoveEvent.canvasY,
                    ),
                    camera,
                    coplanarPoint: mouseDownIntersections[0].point,
                  });

                  return {
                    mouseMovePosition,
                  };
                }),
                map(({ mouseMovePosition }) => {
                  const moveOffset = subVector(
                    mouseMovePosition,
                    mouseDownPosition,
                  );

                  return {
                    moveOffset,
                  };
                }),
                tap(({ moveOffset }) => {
                  setMoveOffset([moveOffset.x, 0, moveOffset.z]);
                }),
                takeUntil(fromEvent<MouseEvent>(window, 'mouseup')),
                defaultIfEmpty({
                  moveOffset: new Vector3(),
                }),
                last(),
                tap(({ moveOffset }) => {
                  const interactionTargetLabelingBoxId =
                    mouseDownIntersections[0].object.userData.id;
                  const orgPostion =
                    mouseDownIntersections[0].object.position.clone();

                  moveBoxList([
                    applyMoveOffsetToBox(
                      moveOffset,
                      orgPostion,
                    )(interactionTargetLabelingBoxId),
                  ]);
                }),
                finalize(() => {
                  cleanupSideMove({
                    toSelectBoxId: mouseDownIntersections[0].object.userData.id,
                  });
                }),
              ),
          ),
          finalize(cleanupOnInvalidStream),
        )
        .subscribe(),
    ],
    [camera, controls, disabled, isAiAssistRunning],
  );

  useEffect(() => {
    labelingBoxListRef.current = labelingBoxList;
  }, [labelingBoxList]);

  return {};
};

export default useDefaultViewSideMove;
